/* eslint-disable jsdoc/require-jsdoc */
import type { HtmlAttributes, HtmlAttributesWithChildren } from '@studio/design-system/props';
import type { Agent } from '@studio/models/agent';
import type { Device } from '#~/device/index.js';
import type { Room } from '@studio/models/room';
import type { Accessor } from '@studio/react-utils';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import type { ReadonlyDeep } from 'type-fest';
import type {
  StatusActiveRecordingVolumes,
  StatusEncoderOperationMode
} from '@tryiris-ai/common.protojs/agent/v1/agent';

/** `<Panel>` properties. */
export type PanelProps = HtmlAttributes<{ agentIds: Agent['id'][]; roomId: Room['id'] }>;

/** `<Monitoring>` properties. */
export type Props = HtmlAttributesWithChildren &
  T_Optional<{
    disabled: boolean;
  }>;

/** Agent statistics. */
export type AgentStats = {
  /** CPU usage in the range `0..100` percent. */
  cpuUsage: string;

  /** Disk space in bytes. */
  diskSpace: number;

  /**
   * Size in bytes of the volume that holds the recording folder, the volume
   * `diskSpace` measures. Absent on Bridges whose firmware predates it; `-1`
   * when the Bridge could not measure it.
   */
  diskTotalSpace?: number;

  /**
   * Free space in bytes at which the Bridge stops recording: the larger of
   * 2 GB and 2% of `diskTotalSpace`, computed by the Bridge. Absent on Bridges
   * whose firmware predates it; `-1` when unknown.
   */
  diskRecordingStopLimit?: number;

  /**
   * The Bridge's authoritative disk gate, including its recovery margin.
   * Absent on older firmware or unavailable telemetry; absence is not a denial.
   */
  diskRecordingBlocked?: boolean;
  /** Active output volumes, independently sampled; absent means unknown. */
  activeRecordingVolumes?: ReadonlyDeep<StatusActiveRecordingVolumes>;

  /** RAM usage in the range `0..100` percent. */
  ramUsage: string;

  /** Version. */
  version: string;

  /** Operation Mode */
  operationMode: StatusEncoderOperationMode;
};

/** @internal */
export type Wiring = Readonly<{
  agents: Record<Agent['id'], Agent>;

  /** Rename agent. */
  handleRename: (id: Agent['id'], next: string) => void;

  /** Restart agent. */
  handleRestart: (id: Agent['id']) => void;

  /** Unlink agent. */
  handleUnlink: (id: Agent['id'], onSuccess?: () => void) => void;

  /** Update agent. */
  handleUpdate: (id: Agent['id']) => void;

  /** Apply suggested settings */
  handleApplySettings: (id: Device['id']) => void;

  /** Enable video stream. */
  handleEnableVideo: (id: Device['id']) => void;

  /** Room state. */
  room?: T_Maybe<
    Readonly<{
      id: Room['id'];
      /** Device tally. */
      tally: { audio: number; video: number };
    }>
  >;

  /** Agent summary data. */
  summary: Readonly<
    T_Optional<{
      agentStats: Record<Agent['id'], AgentStats>;
      audioDevices: Wiring$Device[];
      videoDevices: Wiring$Device[];
      uiVersion: number;
    }>
  >;

  /** Selected tab. */
  tab: Accessor<Tab, string>;
}>;

export interface Wiring$Device {
  /** Agent state. */
  agentId: Agent['id'];

  audio?: T_Maybe<
    T_Optional<{
      bps: number;
      codec: string;
    }>
  >;

  connected: boolean;
  embedded: boolean;

  id: string;

  name: T_Maybe<string>;

  video?: T_Maybe<
    T_Optional<{
      bps: number;
      framerate: string;
      network: number;
      resolution: string;
      source: string;
    }>
  >;

  hasSettingsToApply: boolean;
  videoDisabled: boolean;
}

/** Tabs. */
export enum Tab {
  Audio = 'audio',
  Output = 'output',
  Overview = 'overview',
  Recording = 'recording',
  Video = 'video'
}
